import React, {useState, useEffect, useRef} from 'react';
import {Bar} from 'react-chartjs-2';
import {loadCampaignChartStats} from "../../redux/actions/dashboardActions";
import {useDispatch} from "react-redux";
import {generateLastMonthsLabels} from "../../../shared/helper";
import {Select} from 'antd';
import dayjs from "dayjs";

const barColorInprogress = '#F1BE42';
const barColorNotstarted = '#E5E9ED';
const barColorCompleted = '#58A55C';
const barColorOverdue = '#FF6361';

const options = {
    responsive: true,
    plugins: {
        legend: {
            display: true,
            position: 'top',
            labels: {
                boxWidth: 20,
                padding: 20,
                font: {
                    size: 14
                }
            }
        },
        tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
                title: function (context) {
                    let sum = 0;
                    context.forEach((tooltipItem) => {
                        sum += tooltipItem?.parsed?.y;
                    });
                    return 'Total learners: ' + sum;
                },
                label: function (context) {
                    let label = '';

                    if (context.parsed.y !== null) {
                        label += context.parsed.y;
                    }
                    if (label && context.dataset.label) {
                        label += ' ' + context.dataset.label;
                    }
                    let sum = 0;
                    if (context?.parsed?._stacks?.y) {
                        // only need first 4 element value
                        const arr = context?.parsed?._stacks?.y;
                        sum = arr[0] + arr[1] + arr[2] + arr[3];
                    }
                    let percentage = sum > 0 ? Math.round((context.parsed.y / sum) * 100) + '%' : 0;
                    label += ` (${percentage})`;
                    return label;
                }
            }
        },
        title: {
            display: true,
            // text: 'Training Data Statistics',
            font: {
                size: 18,
                weight: 'bold'
            }
        }
    },
    scales: {
        y: {
            title: {
                display: true,
                text: 'Students',
                font: {
                    size: 14,
                    style: 'normal',
                },
                padding: {top: 30, left: 0, right: 0, bottom: 0}
            },
            stacked: true,
            grid: {
                display: true,
                color: 'rgba(200, 200, 200, 0.25)'
            },
            ticks: {
                beginAtZero: true,
                callback: function (value, index, values) {
                    return value;
                },
                font: {
                    size: 12
                }
            }
        },
        x: {
            stacked: true,
            grid: {
                display: false
            },
            ticks: {
                font: {
                    size: 12
                }
            }
        }
    },
    animation: {
        duration: 1000, // duration of the animation (ms)
        easing: 'easeOutBounce', // type of easing effect
    },
    hover: {
        animationDuration: 300, // duration of animations when hovering an item
        onHover: function (event, activeElements) {
            if (activeElements && activeElements.length) {
                const element = activeElements[0];
                const dataset = this.data.datasets[element._datasetIndex];
                const originalColor = dataset.backgroundColor[element._index];
                dataset.backgroundColor[element._index] = changeColorIntensity(originalColor, 0.2);
            } else {
                this.data.datasets.forEach(dataset => {
                    dataset.backgroundColor = dataset.backgroundColor.map(color => changeColorIntensity(color, 0));
                });
            }
            this.update();
        }
    },
};

function changeColorIntensity(color, intensity) {
    let useHash = false;

    if (color[0] === "#") {
        color = color.slice(1);
        useHash = true;
    }

    // Convert hex to RGB
    let r = parseInt(color.substring(0, 2), 16);
    let g = parseInt(color.substring(2, 4), 16);
    let b = parseInt(color.substring(4, 6), 16);

    // Calculate new RGB values
    r = parseInt(r * (1 + intensity));
    g = parseInt(g * (1 + intensity));
    b = parseInt(b * (1 + intensity));

    // Ensure the values are within the 0-255 range
    r = Math.min(255, Math.max(0, r));
    g = Math.min(255, Math.max(0, g));
    b = Math.min(255, Math.max(0, b));

    // Convert back to hex
    r = r.toString(16).padStart(2, '0');
    g = g.toString(16).padStart(2, '0');
    b = b.toString(16).padStart(2, '0');

    return (useHash ? '#' : '') + r + g + b;
}

const TrainingStatisticsChart = () => {
    const dispatch = useDispatch();
    const chartRef = useRef(null);
    const [chartData, setChartData] = useState({
        labels: generateLastMonthsLabels(),
        datasets: [
            {label: 'Completed', data: [], backgroundColor: barColorCompleted},
            {label: 'In Progress', data: [], backgroundColor: barColorInprogress},
            {label: 'Overdue', data: [], backgroundColor: barColorOverdue},
            {label: 'Not Started', data: [], backgroundColor: barColorNotstarted},
        ]
    });
    const [dataLoaded, setDataLoaded] = useState(false);
    const [campaignOpts, setCampaignOpts] = useState([]);
    const [departmentOpts, setDepartmentOpts] = useState([]);
    const [departmentVal, setDepartmentVal] = useState(0);
    const [campaignVal, setCampaignVal] = useState(0);

    let loadingAjaxLock = false;


    useEffect(() => {
        const chart = chartRef.current;
        console.log(chart);

        if (chart) {
            const ctx = chart.ctx;
            // const patternCanvas = createStripedPattern(ctx, 4, 2, 2, '#F1BE42FF');
            const datasets = chart.data.datasets;
            datasets[1].backgroundColor = createStripedPattern(ctx, 3, 6, 1, "#f1be41");

            chart.update();
        }
    }, [chartData, dataLoaded]);

    useEffect(() => {
        reloadCampaignStates(departmentVal, campaignVal);
    }, [departmentVal, campaignVal]);

    const reloadCampaignStates = (departmentVal = 0, campaignVal = 0) => {
        if (loadingAjaxLock) {
            return;
        }
        loadingAjaxLock = true;
        dispatch(loadCampaignChartStats(campaignVal, departmentVal)).then(result => {
            if (!campaignOpts.length && result.hasOwnProperty('campaigns')) {
                setCampaignOpts([{'value': 0, 'label': 'All Campaign'}, ...result.campaigns]);
            }
            if (!departmentOpts.length && result.hasOwnProperty('departments')) {
                setDepartmentOpts([{'value': 0, 'label': 'All Departments'}, ...result.departments]);
            }
            if (result.hasOwnProperty('statistics')) {
                let data = result.statistics;
                if (!!data) {
                    const months = data.map(item => dayjs(item.month).format('YYYY-MMM'));
                    const notStarted = data.map(item => item.notStarted);
                    const inProgress = data.map(item => item.inProgress);
                    const completed = data.map(item => item.completed);
                    const overdue = data.map(item => item.overdue);
                    setChartData({
                        labels: months,
                        datasets: [
                            {
                                label: 'Completed',
                                data: completed,
                                backgroundColor: barColorCompleted,
                                maxBarThickness: 32,
                                stack: 'Stack 0',
                            },
                            {
                                label: 'In Progress',
                                data: inProgress,
                                backgroundColor: barColorInprogress,
                                maxBarThickness: 32,
                                stack: 'Stack 0',
                            },
                            {
                                label: 'Overdue',
                                data: overdue,
                                backgroundColor: barColorOverdue,
                                maxBarThickness: 32,
                                stack: 'Stack 0',
                            },
                            {
                                label: 'Not Started',
                                data: notStarted,
                                backgroundColor: barColorNotstarted,
                                maxBarThickness: 32,
                                stack: 'Stack 0',
                            },
                        ]
                    });
                }
            }
        }).finally(() => {
            setDataLoaded(true);
            loadingAjaxLock = false;
        }).catch(e => {
            console.log(e);
        })
    }

    return dataLoaded && (
        <div className='training-statistcs-panel'>
            <div className="training-stats-panel-innner">
                <div className="training-stats-panel-header">
                    <h4 className="training-stats-panel-title">Training Progress by Campaign and Department</h4>
                    <div className="filter_wrap">
                        <Select
                            showSearch
                            allowClear
                            placeholder="All campaigns"
                            className={'input-ht-md'}
                            optionFilterProp="children"
                            filterOption={(input, option) => (option?.label ?? '').includes(input)}
                            defaultValue={0}
                            style={{width: 250}}
                            value={campaignVal}
                            onChange={setCampaignVal}
                            options={campaignOpts}
                        />
                        <Select
                            showSearch
                            allowClear
                            placeholder="All departments"
                            className={'input-ht-md'}
                            optionFilterProp="children"
                            filterOption={(input, option) => (option?.label ?? '').includes(input)}
                            defaultValue={0}
                            style={{width: 250}}
                            value={departmentVal}
                            onChange={setDepartmentVal}
                            options={departmentOpts}
                        />
                    </div>
                </div>
                <div className="training-stats-panel-bar-container">
                    <Bar ref={chartRef} data={chartData} options={options}/>
                </div>
            </div>
        </div>
    );
};

export default TrainingStatisticsChart;

function createStripedPattern(ctx, lineWidth, spacing, slope, color) {
    const can = document.createElement('canvas');
    const patternContext = can.getContext('2d');

    const len = Math.hypot(1, slope);

    const w = can.width = 1 / len + spacing + 0.5 | 0; // round to nearest pixel
    const h = can.height = slope / len + spacing * slope + 0.5 | 0;

    patternContext.strokeStyle = color;
    patternContext.lineWidth = lineWidth;
    patternContext.beginPath();

    // Line through top left and bottom right corners
    patternContext.moveTo(0, 0);
    patternContext.lineTo(w, h);
    // Line through top right corner to add missing pixels
    patternContext.moveTo(0, -h);
    patternContext.lineTo(w * 2, h);
    // Line through bottom left corner to add missing pixels
    patternContext.moveTo(-w, 0);
    patternContext.lineTo(w, h * 2);

    patternContext.stroke();

    return ctx.createPattern(can, 'repeat');
}
